import React, { useState } from 'react';
import { AlertTriangle, Eye, Edit3, Copy, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';

const DuplicateSKUModal = ({
  open,
  onClose,
  conflictData,
  originalSKU,
  onResolveAction
}) => {
  const [selectedAction, setSelectedAction] = useState(null);
  const [newSKU, setNewSKU] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const { existing_product, suggested_actions = [] } = conflictData || {};

  // Generate suggested SKU variations
  const generateSKUVariations = (baseSKU) => {
    const variations = [
      `${baseSKU}-V2`,
      `${baseSKU}-NEW`,
      `${baseSKU}-${new Date().getFullYear()}`,
      `${baseSKU}-ALT`,
      `${baseSKU}-COPY`
    ];
    return variations;
  };

  const handleActionSelect = (action) => {
    setSelectedAction(action);
    if (action === 'rename_sku') {
      // Pre-populate with first suggested variation
      const variations = generateSKUVariations(originalSKU);
      setNewSKU(variations[0]);
    }
  };

  const handleResolve = async () => {
    if (!selectedAction) {
      toast.error('Please select an action');
      return;
    }

    if (selectedAction === 'rename_sku' && !newSKU.trim()) {
      toast.error('Please enter a new SKU');
      return;
    }

    setIsProcessing(true);

    try {
      const resolution = {
        action: selectedAction,
        ...(selectedAction === 'rename_sku' && { new_sku: newSKU.trim() }),
        existing_product: existing_product
      };

      await onResolveAction(resolution);
      toast.success('SKU conflict resolved');
      onClose();
    } catch (error) {
      toast.error('Failed to resolve conflict');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = () => {
    setSelectedAction(null);
    setNewSKU('');
    onClose();
  };

  if (!open || !conflictData) return null;

  return (
    <Dialog open={open} onOpenChange={handleCancel}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="w-5 h-5" />
            SKU Conflict Detected
          </DialogTitle>
          <DialogDescription>
            The SKU "{originalSKU}" is already in use. Please choose how to resolve this conflict.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Existing Product Info */}
          <Card className="border-yellow-200 bg-yellow-50">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-yellow-800">Existing Product</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">{existing_product?.name || 'Unknown Product'}</p>
                  <p className="text-sm text-gray-600">SKU: {existing_product?.sku}</p>
                  <p className="text-xs text-gray-500">
                    Created: {existing_product?.created_at ? 
                      new Date(existing_product.created_at).toLocaleDateString() : 
                      'Unknown'
                    }
                  </p>
                </div>
                <Badge variant={existing_product?.status === 'active' ? 'default' : 'secondary'}>
                  {existing_product?.status || 'Unknown'}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Resolution Options */}
          <div className="space-y-3">
            <Label className="text-base font-medium">Choose Resolution:</Label>
            
            {suggested_actions.map((action, index) => {
              const isSelected = selectedAction === action.action;
              
              return (
                <Card 
                  key={action.action}
                  className={`cursor-pointer transition-all border-2 ${
                    isSelected 
                      ? 'border-blue-500 bg-blue-50 shadow-md' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => handleActionSelect(action.action)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="mt-1">
                        {action.action === 'open_existing' && <Eye className="w-5 h-5 text-blue-600" />}
                        {action.action === 'rename_sku' && <Edit3 className="w-5 h-5 text-green-600" />}
                        {action.action === 'create_variant' && <Copy className="w-5 h-5 text-purple-600" />}
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h3 className="font-medium text-gray-900">{action.label}</h3>
                          <input
                            type="radio"
                            name="action"
                            checked={isSelected}
                            onChange={() => handleActionSelect(action.action)}
                            className="text-blue-600"
                          />
                        </div>
                        
                        <p className="text-sm text-gray-600 mt-1">
                          {action.action === 'open_existing' && 'Navigate to the existing product for editing'}
                          {action.action === 'rename_sku' && 'Create the product with a different SKU'}
                          {action.action === 'create_variant' && 'Create as a variant of the existing product'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* SKU Rename Input */}
          {selectedAction === 'rename_sku' && (
            <Card className="border-green-200 bg-green-50">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm text-green-800">New SKU</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label htmlFor="new-sku">Enter New SKU:</Label>
                  <Input
                    id="new-sku"
                    value={newSKU}
                    onChange={(e) => setNewSKU(e.target.value)}
                    placeholder="Enter unique SKU"
                    className="mt-1"
                  />
                </div>
                
                {/* SKU Suggestions */}
                <div>
                  <Label className="text-xs text-gray-600">Suggested variations:</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {generateSKUVariations(originalSKU).map((suggestion, index) => (
                      <Button
                        key={index}
                        variant="outline"
                        size="sm"
                        onClick={() => setNewSKU(suggestion)}
                        className="text-xs"
                      >
                        {suggestion}
                      </Button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Create Variant Details */}
          {selectedAction === 'create_variant' && (
            <Card className="border-purple-200 bg-purple-50">
              <CardContent className="pt-4">
                <p className="text-sm text-purple-800">
                  This will create a new variant linked to the existing product "{existing_product?.name}".
                  The variant will share the same base information but can have different specifications.
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t">
          <Button variant="outline" onClick={handleCancel} disabled={isProcessing}>
            <X className="w-4 h-4 mr-2" />
            Cancel
          </Button>
          <Button 
            onClick={handleResolve} 
            disabled={!selectedAction || isProcessing}
            className={`${
              selectedAction === 'open_existing' ? 'bg-blue-600 hover:bg-blue-700' :
              selectedAction === 'rename_sku' ? 'bg-green-600 hover:bg-green-700' :
              selectedAction === 'create_variant' ? 'bg-purple-600 hover:bg-purple-700' :
              ''
            }`}
          >
            {isProcessing ? 'Processing...' : 'Resolve Conflict'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DuplicateSKUModal;